import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSaved } from "@/lib/useSaved";
import { BarScore, RadialScore, scoreColor } from "@/components/ScoreMeter";
import ProvenanceTag from "@/components/ProvenanceTag";
import AnalysisReport from "@/components/AnalysisReport";
import { Button } from "@/components/ui/button";
import {
  Sparkles, Bookmark, FolderPlus, Loader2, ArrowLeft, AlertTriangle, Lightbulb,
  Target, Users, TrendingUp, Wrench, DollarSign, ShieldAlert, FlaskConical, Layers, Share2,
} from "lucide-react";
import { toast } from "sonner";

export default function OpportunityDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { savedIds, toggleSave } = useSaved();
  const [opp, setOpp] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get(`/opportunities/${slug}`).then((r) => setOpp(r.data)).catch(() => navigate("/discover"));
  }, [slug, navigate]);

  const analyze = async () => {
    if (!user) { navigate("/login"); return; }
    setAnalyzing(true);
    try {
      const res = await api.post(`/ai/analyze/${slug}`);
      setAnalysis(res.data.report);
      toast.success("AI analysis complete");
      setTimeout(() => document.getElementById("ai-analysis")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analysis failed");
    } finally { setAnalyzing(false); }
  };

  const turnIntoProject = async () => {
    if (!user) { navigate("/login"); return; }
    setCreating(true);
    try {
      await api.post("/projects", { opportunity_slug: slug, title: opp.title });
      toast.success("Project created in your workspace");
      navigate("/dashboard");
    } catch { toast.error("Could not create project"); }
    finally { setCreating(false); }
  };

  if (!opp) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" /></div>;

  const s = opp.scores || {};
  const color = scoreColor(opp.overall_score);
  const saved = savedIds.has(opp.slug);
  const isCommunity = opp.source === "community";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link to="/discover" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-cyan-300 mb-6"><ArrowLeft className="w-4 h-4" />Back to feed</Link>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <span className="text-xs font-mono uppercase tracking-wider text-cyan-400 px-2.5 py-1 rounded bg-cyan-500/10 border border-cyan-500/20">{opp.category}</span>
            {opp.trending && <span className="text-xs font-mono text-emerald-400 flex items-center gap-1"><TrendingUp className="w-3 h-3" />Trending</span>}
            <ProvenanceTag type={isCommunity ? "community" : "verified"} />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-50 leading-tight mb-3">{opp.title}</h1>
          <p className="text-lg text-slate-300 leading-relaxed mb-4">{opp.description}</p>
          <div className="flex flex-wrap gap-2 mb-6">
            {(opp.tags || []).map((t) => <span key={t} className="text-xs font-mono text-slate-400 px-2 py-1 rounded bg-white/5 border border-white/8">#{t}</span>)}
          </div>
          <div className="flex flex-wrap gap-3">
            <Button onClick={analyze} disabled={analyzing} data-testid="analyze-ai-btn" className="bg-gradient-to-r from-cyan-500 to-indigo-500 hover:opacity-90 text-slate-950 font-semibold">
              {analyzing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analyzing…</> : <><Sparkles className="w-4 h-4 mr-2" />Analyze With AI</>}
            </Button>
            <Button onClick={() => toggleSave(opp)} variant="outline" className="border-white/15 text-slate-200" data-testid="detail-save-btn">
              <Bookmark className="w-4 h-4 mr-2" fill={saved ? "currentColor" : "none"} />{saved ? "Saved" : "Save"}
            </Button>
            <Button onClick={turnIntoProject} disabled={creating} variant="outline" className="border-white/15 text-slate-200" data-testid="turn-into-project-btn">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FolderPlus className="w-4 h-4 mr-2" />}Turn Into Project
            </Button>
            <Button onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success("Link copied"); }} variant="ghost" className="text-slate-400"><Share2 className="w-4 h-4" /></Button>
          </div>
        </div>

        <div className="glass rounded-2xl p-6">
          <div className="flex flex-col items-center mb-5">
            <RadialScore value={opp.overall_score} size={128} stroke={10} label="Opportunity" testId="overall-score" />
            <p className="text-sm font-semibold mt-2" style={{ color }}>{opp.overall_score >= 80 ? "Strong Opportunity" : opp.overall_score >= 60 ? "Solid Opportunity" : "Niche Opportunity"}</p>
          </div>
          <div className="space-y-3">
            <BarScore label="Demand" value={s.demand} testId="score-demand" />
            <BarScore label="Competition" value={s.competition} invert testId="score-competition" />
            <BarScore label="Monetization" value={s.monetization} testId="score-monetization" />
            <BarScore label="Build Difficulty" value={s.build_difficulty} invert testId="score-build" />
            <BarScore label="Trend Velocity" value={s.trend_velocity} testId="score-trend" />
            <BarScore label="Search Demand" value={s.search_demand} />
            <BarScore label="Customer Pain" value={s.pain_level} />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-5 pt-5 border-t border-white/8 text-center">
            <div><p className="text-[10px] uppercase tracking-wide text-slate-500">Revenue</p><p className="text-sm font-mono text-emerald-400">{opp.revenue_potential}</p></div>
            <div><p className="text-[10px] uppercase tracking-wide text-slate-500">MVP Time</p><p className="text-sm font-mono text-cyan-400">{opp.mvp_time}</p></div>
            <div className="col-span-2"><p className="text-[10px] uppercase tracking-wide text-slate-500">Pricing</p><p className="text-sm font-mono text-slate-200">{opp.pricing}</p></div>
          </div>
        </div>
      </div>

      {!isCommunity && (
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <Narrative icon={Lightbulb} title="Why this opportunity exists" text={opp.why_exists} />
          <Narrative icon={Users} title="Who has the problem" text={opp.who_has_problem} />
          <Narrative icon={Target} title="How existing solutions fall short" text={opp.existing_gaps} />
          <Narrative icon={Wrench} title="What to build" text={opp.what_to_build} />
          <Narrative icon={DollarSign} title="How to monetize" text={opp.how_to_monetize} />
          <Narrative icon={TrendingUp} title="How to acquire customers" text={opp.how_to_acquire} />
          <Narrative icon={Layers} title="Suggested MVP" text={opp.suggested_mvp} />
          <Narrative icon={FlaskConical} title="Validation strategy" text={opp.validation_strategy} />
          <ListCard icon={Wrench} title="Suggested Tech Stack" items={opp.tech_stack} mono />
          <ListCard icon={Swords} title="Potential Competitors" items={opp.competitors} />
          <ListCard icon={ShieldAlert} title="Risks" items={opp.risks} className="md:col-span-2" />
        </div>
      )}

      <div id="ai-analysis" className="scroll-mt-20">
        {analyzing && !analysis && (
          <div className="glass rounded-xl p-10 text-center">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin mx-auto mb-3" />
            <p className="text-slate-300">Nova is running a full opportunity analysis…</p>
            <p className="text-sm text-slate-500 mt-1">Evaluating market, competition, customers, product, monetization & GTM.</p>
          </div>
        )}
        {analysis && (
          <div className="fade-up">
            <h2 className="font-display text-2xl font-bold text-slate-50 mb-4 flex items-center gap-2"><Sparkles className="w-6 h-6 text-cyan-400" />AI Opportunity Analysis</h2>
            <AnalysisReport report={analysis} />
          </div>
        )}
      </div>
    </div>
  );
}

function Swords(props) { return <ShieldAlert {...props} />; }

function Narrative({ icon: Icon, title, text }) {
  if (!text) return null;
  return (
    <div className="glass rounded-xl p-5">
      <div className="flex items-center gap-2 mb-2"><Icon className="w-4 h-4 text-cyan-400" /><h4 className="font-semibold text-slate-100 text-sm">{title}</h4></div>
      <p className="text-sm text-slate-300 leading-relaxed">{text}</p>
    </div>
  );
}

function ListCard({ icon: Icon, title, items, mono, className = "" }) {
  if (!items || !items.length) return null;
  return (
    <div className={`glass rounded-xl p-5 ${className}`}>
      <div className="flex items-center gap-2 mb-3"><Icon className="w-4 h-4 text-cyan-400" /><h4 className="font-semibold text-slate-100 text-sm">{title}</h4></div>
      <div className="flex flex-wrap gap-2">
        {items.map((it, i) => <span key={i} className={`text-xs px-2.5 py-1 rounded bg-white/5 border border-white/8 text-slate-300 ${mono ? "font-mono" : ""}`}>{it}</span>)}
      </div>
    </div>
  );
}
